// Sugalarm — Clock Face Waves (cycle 265).
//
// The animated blue-wave background shared by BOTH bedside clock faces:
// the portrait face (PortraitClockFace) and the landscape face
// (AlarmClockMode). Extracted from the cycle-253/255 portrait canvas so
// the two faces can never drift apart, and REDESIGNED for a calm,
// natural RIGHT-TO-LEFT flow.
//
// Cycle 255 had stacked three golden-ratio (φ, φ²) oscillators with
// per-layer independent clocks specifically so the crest pattern would
// NEVER repeat — which read as chaotic noise, not water. This version
// reverses that: ONE shared clock, every layer + every harmonic drifts
// the SAME way (leftward), so the whole stack flows coherently across the
// face like gentle ocean swells, with slight per-layer speed differences
// for parallax depth.
//
// PURELY PRESENTATIONAL — owns no clock/alarm/glucose state. Fills its
// positioned parent; the caller supplies a className for placement
// (absolute/fixed inset:0 behind the content).

import { useEffect, useRef } from "react";

// Back-to-front translucent ramp: deep midnight navy at the bottom →
// pale cyan-blue at ~70% height. Literal RGBA is the one allowed
// exception (canvas draw code, for performance). Alpha values are the
// shipped cycle-255 "barely-there atmospheric texture" levels (≈25% of
// the original Cycle-253 alphas) — UNCHANGED here; this cycle only
// reworks the MOTION, not the brightness. The `#090d12` ground dominates.
const WAVE_COLORS = [
  "rgba(8,18,60,0.22)",
  "rgba(14,36,100,0.20)",
  "rgba(22,58,160,0.18)",
  "rgba(29,90,210,0.17)",
  "rgba(37,120,235,0.15)",
  "rgba(56,155,240,0.12)",
  "rgba(96,190,245,0.09)",
  "rgba(147,218,253,0.055)",
];
const GROUND = "#090d12";

// Cycle 265: per-layer motion params for a calm leftward flow. Back
// (deep navy, bottom) → front (pale cyan, ~70% up). `speed` increases
// gently front-to-back-reversed so the nearer (front) layers travel a
// touch faster than the far ones — parallax depth — but EVERY layer
// flows the same way (leftward), so the stack reads as one coherent
// current rather than counter-drifting noise. Long wavelengths (low
// `freq`) give broad, calm swells.
const WAVE_PARAMS = [
  { speed: 0.28, freq: 0.0042, amp: 13, phase: 0.0 },
  { speed: 0.31, freq: 0.0048, amp: 15, phase: 0.7 },
  { speed: 0.34, freq: 0.0044, amp: 14, phase: 1.5 },
  { speed: 0.37, freq: 0.0052, amp: 17, phase: 2.4 },
  { speed: 0.40, freq: 0.0047, amp: 15, phase: 3.1 },
  { speed: 0.44, freq: 0.0056, amp: 18, phase: 3.9 },
  { speed: 0.48, freq: 0.0050, amp: 16, phase: 4.7 },
  { speed: 0.52, freq: 0.0060, amp: 19, phase: 5.5 },
];

interface Props {
  /** Placement class (absolute/fixed inset:0 behind the face content). */
  className?: string;
}

export function ClockFaceWaves({ className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    // ONE shared clock for the whole stack — every layer reads the same
    // `t`, so they all flow leftward together (a coherent current, not
    // the cycle-255 per-layer drift-apart). Persists across frames within
    // this effect; reset to 0 on remount → the reduced-motion static frame.
    let t = 0;
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const sizeCanvas = (): { w: number; h: number } => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };

    const drawFrame = (w: number, h: number): void => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = GROUND;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < WAVE_COLORS.length; i++) {
        const wp = WAVE_PARAMS[i]!;
        const frac = i / (WAVE_COLORS.length - 1);
        // Slow, gentle vertical sway so the band breathes; phase-offset
        // per layer so they don't bob in unison.
        const baseY = h * (0.97 - 0.66 * frac) + 6 * Math.sin(t * 0.1 + wp.phase);
        // Gentle amplitude breathe (kept small — the flow, not the pulse,
        // is the motion).
        const amp = wp.amp * (1 + 0.08 * Math.sin(t * 0.16 + wp.phase * 1.3));

        ctx.beginPath();
        ctx.moveTo(0, h);
        for (let x = 0; x <= w; x += 6) {
          // Primary swell + one SAME-DIRECTION secondary harmonic (both
          // `+ t * speed` → both travel LEFT). The secondary runs a touch
          // faster at a higher frequency, so crests morph organically
          // while the whole wave still flows clearly right-to-left.
          const y1 = amp * Math.sin(x * wp.freq + t * wp.speed + wp.phase);
          const y2 =
            amp *
            0.32 *
            Math.sin(x * wp.freq * 1.7 + t * wp.speed * 1.3 + wp.phase * 1.6);
          ctx.lineTo(x, baseY + y1 + y2);
        }
        ctx.lineTo(w, h);
        ctx.closePath();
        ctx.fillStyle = WAVE_COLORS[i]!;
        ctx.fill();
      }
    };

    let dims = sizeCanvas();
    const onResize = (): void => {
      dims = sizeCanvas();
      if (reduce) drawFrame(dims.w, dims.h);
    };
    window.addEventListener("resize", onResize);

    if (reduce) {
      // No animation loop — a single static silhouette over the ground
      // (t = 0 frame).
      drawFrame(dims.w, dims.h);
    } else {
      const loop = (): void => {
        drawFrame(dims.w, dims.h);
        // One gentle increment for the whole stack — the shared clock is
        // what keeps every layer flowing the same way.
        t += 0.016;
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
